import { z } from 'zod';
/**
 * Global search — request + response schemas for `POST /api/orgs/:o/search`.
 *
 * Distinct from `packages/shared/src/search.ts` (which is the row-kind
 * vocabulary for the legacy `<SearchResultsOverlay>` rail). This module
 * carries the wire shape the full-page `/search` surface POSTs against:
 * a free-text query + four-facet filter bag (type / time / confidence /
 * source) returning a synthesized cited agent answer + the raw result
 * rows that grounded the synthesis.
 *
 * Closed-set rule: each filter facet is a `z.enum` whose values match the
 * client's `<SearchFilterRail>` vocabulary 1:1. The `type` facet uses
 * `'all'` as its catch-all (not `'everything'`, which the rail uses
 * cosmetically) so the wire vocabulary lines up with the other facets.
 * The rail-side mapping lives in `apps/web/src/lib/api.ts`.
 *
 * Lives in its own subdirectory so the `./search.ts` row-kind enums and
 * the global-search schemas don't collide on a single barrel.
 */
/** Type facet — which result kinds to include. Mirrors the rail. */
export const GlobalSearchTypeFilter = z.enum([
    'all',
    'insights',
    'dossiers',
    'notes',
    'threads',
    'decisions',
    'files',
]);
/** Time facet — recency cutoff applied server-side against `updatedAt`. */
export const GlobalSearchTimeFilter = z.enum([
    'all',
    'today',
    'last-7d',
    'last-30d',
    'quarter',
    'year',
]);
/** Confidence facet — gates agent-synthesized rows (insights, decisions). */
export const GlobalSearchConfidenceFilter = z.enum(['all', 'high', 'medium', 'low']);
/**
 * Result-row kind discriminator. Same vocabulary as the agent-answer
 * citations so the client can dedupe between the citation pills and the
 * row list. `audit-event` covers `agent_audit_events` rows — agent
 * activity surfaced inside the search results, NOT a connector source.
 */
export const GlobalSearchResultKind = z.enum([
    'insight',
    'dossier',
    'note',
    'audit-event',
    'decision',
    'file',
]);
/**
 * Inbound search request. `query` is the canonical text the synthesis
 * grounds against; the facets server-filter the candidate set BEFORE the
 * Vertex call so the model only sees rows the user opted in to.
 */
export const GlobalSearchRequest = z.object({
    query: z.string().min(1).max(500),
    type: GlobalSearchTypeFilter.default('all'),
    time: GlobalSearchTimeFilter.default('all'),
    confidence: GlobalSearchConfidenceFilter.default('all'),
    /**
     * Connector kind filter (`slack` / `gmail` / `salesforce` / `github` /
     * `stripe`). Optional + open-typed — the connector-kind enum lives in
     * `packages/shared/src/connectors/kind.ts` but the search surface also
     * has to handle non-connector rows (notes, decisions, insights) where
     * "source" isn't meaningful. Leaving it open-string keeps the wire
     * forward-compatible with future connector kinds without a schema bump.
     */
    source: z.string().max(64).optional(),
});
/**
 * One citation backing the agent answer. `index` is 1-based to match the
 * `[N]` markers the prompt asks the model to emit; the client `<AgentAnswerCard>`
 * splits the answer text on `[\d+]` and looks up the citation by index.
 */
export const GlobalSearchCitation = z.object({
    index: z.number().int().min(1),
    kind: GlobalSearchResultKind,
    /** Opaque pointer back to the source row (doc id / entity id). */
    ref: z.string().min(1).max(400),
    /** Human-readable label for the source-badge pill. */
    label: z.string().min(1).max(200),
});
/**
 * One raw result row. The client renders these below the agent answer
 * card; each row wraps its title through `<EntityName>` so name
 * resolution + the confidence pill stay consistent across surfaces.
 */
export const GlobalSearchResult = z.object({
    kind: GlobalSearchResultKind,
    ref: z.string().min(1).max(400),
    title: z.string().min(1).max(400),
    /** 1-2 sentence excerpt with the matched substring. */
    snippet: z.string().max(800),
    /** ISO timestamp of last update — drives the time-filter facet. */
    matchedAt: z.string(),
});
/**
 * Outbound response. `agentAnswer` is undefined when the candidate set
 * was too thin (< `MIN_RESULTS_FOR_SYNTHESIS`) — the page degrades to
 * "no agent answer; here are the raw matches" rather than synthesizing
 * a confident-sounding answer from one source.
 */
export const GlobalSearchResponse = z.object({
    query: z.string(),
    /** 2-3 sentence synthesized paragraph with inline `[N]` citation markers. */
    agentAnswer: z.string().optional(),
    /** 0..1 self-assessment from the model. Powers `<ConfidenceMeter>`. */
    agentAnswerConfidence: z.number().min(0).max(1).optional(),
    /** Top citations referenced in `agentAnswer`. Capped at 5. */
    citations: z.array(GlobalSearchCitation).max(5).default([]),
    /** Full ranked result list (capped server-side). */
    results: z.array(GlobalSearchResult).default([]),
    totalResults: z.number().int().min(0),
    /** Server-side latency in ms (search + synthesis + serialization). */
    tookMs: z.number().int().min(0),
});
/**
 * Minimum candidate-count threshold for triggering the Vertex synthesis.
 * Below this we ship raw results only — synthesizing a "confident"
 * answer from a single doc would over-claim, and the CEO is better
 * served by the raw row.
 */
export const MIN_RESULTS_FOR_SYNTHESIS = 3;
/** Hard cap on result rows returned per request. */
export const GLOBAL_SEARCH_MAX_RESULTS = 50;
/** Hard cap on agent-answer body length to keep the card legible. */
export const GLOBAL_SEARCH_ANSWER_MAX_CHARS = 600;
